/** Small builders shared by the class files. */
import type { AbilityDef, AreaSpec, AuraDef, EffectSpec, Ms, StatMod } from '@keystone/sim';
import { ABILITY } from '../ids';

/** Off the global cooldown. */
export const OFF_GCD = 0;
/** Standard spell range. */
export const SPELL_RANGE = 40;

/** Circle around the caster hitting enemies (closest first). */
export const aroundSelf = (radius: number, maxTargets?: number): AreaSpec => ({
  anchor: 'caster',
  shape: { type: 'circle', radius },
  affects: 'enemies',
  ...(maxTargets ? { maxTargets } : {}),
});

/** Circle around the primary target hitting enemies. */
export const aroundTarget = (radius: number, maxTargets?: number): AreaSpec => ({
  anchor: 'target',
  shape: { type: 'circle', radius },
  affects: 'enemies',
  ...(maxTargets ? { maxTargets } : {}),
});

/** A self-buff that only carries stat mods. */
export function selfBuff(id: string, name: string, durationMs: Ms, mods: StatMod[], extra: Partial<AuraDef> = {}): EffectSpec {
  return {
    type: 'applyAura',
    to: 'self',
    aura: { id, name, kind: 'buff', durationMs, stacking: 'refresh', mods, ...extra },
  };
}

/** A helpful aura on the ability's (ally) target. */
export function allyBuff(id: string, name: string, durationMs: Ms, mods: StatMod[], extra: Partial<AuraDef> = {}): EffectSpec {
  return {
    type: 'applyAura',
    aura: { id, name, kind: 'buff', durationMs, stacking: 'unique', mods, ...extra },
  };
}

export const interrupt = (lockoutMs: Ms): EffectSpec => ({ type: 'interrupt', lockoutMs });

/** Dodge (Space): how far it carries you, and how fast. */
export const DODGE_DISTANCE = 8;
export const DODGE_SPEED = 40;

/**
 * Dodge, shared by every hero: a short dash toward the point the client sends (the held move
 * direction, else straight ahead). Off the GCD, 2 charges; the forced move cancels a cast in
 * progress and it can't be used while rooted or stunned.
 */
export const dodge: AbilityDef = {
  id: ABILITY.common.dodge,
  name: 'Dodge',
  school: 'physical',
  description: 'Dash a short distance in the direction you are moving (forward if standing still).',
  targeting: 'ground',
  range: DODGE_DISTANCE,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 10000, charges: 2 },
  requiresMobility: true,
  // Usable mid-cast; the dash's forced move then cancels the cast (sim casting update).
  usableWhileCasting: true,
  effects: [{ type: 'dash', mode: 'toPoint', distance: DODGE_DISTANCE, speed: DODGE_SPEED }],
  ui: { tags: ['movement'], color: '#9fd3ff' },
};
